import { useEffect, useState } from 'react';
import { listMicInputs, type MicInputs } from '@clarix/meeting-recording';
import { QM_MIC } from './constants';

export interface MicDevices extends MicInputs {
  /** The list has been read: without permission the browser hides labels, so it is never read then. */
  listed: boolean;
}

const UNREAD: MicDevices = { devices: [], systemDefault: null, listed: false };

/** Whether the site already holds mic permission; false where the browser can't say. */
function useMicPermissionGranted(): boolean {
  const [granted, setGranted] = useState(false);
  useEffect(() => {
    let live = true;
    let status: PermissionStatus | null = null;
    const onChange = () => {
      if (live && status) setGranted(status.state === 'granted');
    };
    try {
      void navigator.permissions
        ?.query({ name: 'microphone' as PermissionName })
        .then((s) => {
          if (!live) return;
          status = s;
          setGranted(s.state === 'granted');
          s.addEventListener('change', onChange);
        })
        .catch(() => undefined);
    } catch {
      /* 'microphone' is not a queryable name in every browser */
    }
    return () => {
      live = false;
      status?.removeEventListener('change', onChange);
    };
  }, []);
  return granted;
}

/**
 * The mic inputs, read only once permission is already granted (or a capture is running), so the
 * picker never opens a capture just to learn labels. Re-read when devices settle after a change,
 * and when a capture starts or ends (Chrome can re-salt groupIds then).
 */
export function useMicDevices(capturing: boolean): MicDevices {
  const granted = useMicPermissionGranted();
  const [inputs, setInputs] = useState<MicDevices>(UNREAD);
  const enabled = granted || capturing;

  useEffect(() => {
    if (!enabled) return;
    let live = true;
    let timer = 0;
    const read = () => {
      void listMicInputs().then((next) => {
        if (live) setInputs({ ...next, listed: true });
      });
    };
    const onDeviceChange = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(read, QM_MIC.DEVICE_CHANGE_DEBOUNCE_MS);
    };
    read();
    const devices = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices;
    devices?.addEventListener('devicechange', onDeviceChange);
    return () => {
      live = false;
      window.clearTimeout(timer);
      devices?.removeEventListener('devicechange', onDeviceChange);
    };
  }, [enabled, capturing]);

  return inputs;
}
